import { useState } from 'react'
import Image from 'components/image'
import { LinkButton, InfoButton } from 'components/buttons'
import { Form, Input } from 'components/forms'
import classNames from 'classnames'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { track } from 'lib/analytics'
import { registryAddItemsPath } from 'lib/urls'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import Slide, { Title } from '../../Slide/Slide'
import css from './FinishingTouches.styles.scss'
import * as api from '../../../api'
import { useNavigate } from 'react-router-dom-v5-compat'

interface FinishingTouchesProps {
  isInActEx5PartnerExperiment?: boolean
  nextPath: string
}

export const FinishingTouches = ({
  isInActEx5PartnerExperiment = false,
  nextPath,
}: FinishingTouchesProps) => {
  const [currentUser] = useCurrentUser()
  const [submitting, setSubmitting] = useState(false)
  const [showPartnerForm, setShowPartnerForm] = useState<boolean>()
  const [showPartnerFormEmailHelperText, setShowPartnerFormEmailHelperText] =
    useState(false)
  const [showCollaboratorForm, setShowCollaboratorForm] = useState<boolean>()
  const [
    showCollaboratorFormEmailHelperText,
    setShowCollaboratorFormEmailHelperText,
  ] = useState(false)
  const [formError, setFormError] = useState<string>()

  const { register, handleSubmit, watch, errors } = useForm()
  const registry = useSelector((state: any) => state.registry)

  const navigate = useNavigate()

  const goToNext = () => {
    if (nextPath === registryAddItemsPath) {
      window.location.href = nextPath // This is a different page and not part of the onboarding Wizard
    } else {
      navigate(nextPath)
    }
  }

  const onSkip = () => {
    const registryId = registry?.id || currentUser?.currentRegistry?.id

    if (!registryId) {
      // If we still don't have a registryId, allow the user to skip
      return goToNext()
    }

    // saveHasPartner only needs the id of the registry to update
    api.saveHasPartner({ registry: { id: registryId }, hasPartner: false })
    goToNext()
  }

  const onSubmit = (data: any) => {
    setSubmitting(true)

    // If both options are no or blank, skip
    if (!showPartnerForm && !showCollaboratorForm) {
      onSkip()
      return
    }

    const partner = showPartnerForm
      ? {
          firstName: data.partnerFirstName,
          lastName: data.partnerLastName,
          email: data.partnerEmailAddress,
        }
      : null
    const collaborator = showCollaboratorForm
      ? {
          firstName: data.collaboratorFirstName,
          lastName: data.collaboratorLastName,
          email: data.collaboratorEmailAddress,
        }
      : null

    const registryId = registry?.id || currentUser?.currentRegistry?.id

    api
      .finishingTouches({ registryId, partner, collaborator })
      .then(() => {
        setSubmitting(false)
        setFormError(undefined)
        track.onboardingQuestionAnswered({
          eventLocation:
            track.EventLocation.ONBOARDING_MINUS_PARTNER_EMAIL_COLLECTION,
        })
        if (showPartnerForm)
          track.collaboratorInvited({
            collaboratorRole: track.CollaboratorRole.PARTNER,
          })
        if (showCollaboratorForm)
          track.collaboratorInvited({
            collaboratorRole: track.CollaboratorRole.COLLABORATOR,
          })
        goToNext()
      })
      .catch((error) => {
        setSubmitting(false)
        setFormError(error._error)
      })
  }

  const validateEmail = (
    email: string,
    otherEmail: string
  ): string | undefined => {
    if (email.length === 0) return undefined
    if (email.toLowerCase() === otherEmail.toLowerCase()) {
      if (!showPartnerForm || !showCollaboratorForm) {
        return undefined
      }
      return `You can't use the same email for partner and collaborator.`
    }
    if (email.toLowerCase() === currentUser?.email?.toLowerCase()) {
      return "Whoops! You can't add yourself!"
    }
    return undefined
  }

  const collaboratorEmailError =
    errors.collaboratorEmailAddress &&
    (errors.collaboratorEmailAddress.message || 'Email address is required.')

  return (
    <Slide className={css.FinishingTouches__slide}>
      <div className={css.FinishingTouches__container}>
        <div className={css.FinishingTouches__headerContainer}>
          <Title className={css.FinishingTouches__title}>
            Time for some finishing touches!
          </Title>
          <p className={css.FinishingTouches__subTitle}>
            Let's make this registry truly yours.
          </p>
        </div>
        <Form
          className={css.FinishingTouches__form}
          error={formError}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={css.FinishingTouches__formFieldsContainer}>
            <div className={css.FinishingTouches__questionContainer}>
              <p className={css.FinishingTouches__question}>
                Do you have a partner you would like listed on your registry?
              </p>
              <p className={css.FinishingTouches__questionSubText}>
                Your friends and family can find your registry using your
                partner's name. They can also help build your registry.
              </p>
            </div>
            <div className={css.FinishingTouches__buttonContainer}>
              <InfoButton
                className={classNames(css.FinishingTouches__button, {
                  [css.FinishingTouches__button__active]: showPartnerForm,
                  [css.FinishingTouches__button__inactive]:
                    showPartnerForm === false,
                })}
                type="button"
                onClick={() => setShowPartnerForm(true)}
              >
                Yes
              </InfoButton>
              <InfoButton
                className={classNames(css.FinishingTouches__button, {
                  [css.FinishingTouches__button__active]:
                    showPartnerForm === false,
                  [css.FinishingTouches__button__inactive]: showPartnerForm,
                })}
                type="button"
                onClick={() => setShowPartnerForm(false)}
              >
                No
              </InfoButton>
            </div>
            <div
              className={classNames(css.FinishingTouches__inputContainer, {
                [css.FinishingTouches__inputContainer__show]: showPartnerForm,
              })}
            >
              <Input
                error={errors.partnerFirstName && 'First name is required.'}
                inputRef={register({ required: showPartnerForm })}
                label="Partner's first name"
                name="partnerFirstName"
                type="text"
              />
              <Input
                error={errors.partnerLastName && 'Last name is required.'}
                inputRef={register({ required: showPartnerForm })}
                label="Partner's last name"
                name="partnerLastName"
                type="text"
              />
              <Input
                error={
                  errors.partnerEmailAddress &&
                  errors.partnerEmailAddress.message
                }
                help={
                  showPartnerFormEmailHelperText
                    ? 'Email address is required for them to add items to your registry.'
                    : ''
                }
                inputRef={register({
                  validate: (email: string) =>
                    validateEmail(email, watch('collaboratorEmailAddress')),
                })}
                label="Want them to help build your registry?"
                name="partnerEmailAddress"
                placeholder="Email address (optional)"
                type="email"
                onBlur={() => setShowPartnerFormEmailHelperText(false)}
                onFocus={() => setShowPartnerFormEmailHelperText(true)}
              />
            </div>
            <Image
              alt="Divider"
              className={css.FinishingTouches__formFieldsDivider}
              src="https://images.babylist.com/image/upload/f_auto,q_auto/v1691183576/Onboarding/gray_squiggle_divider.png"
              width={135}
            />
            <div className={css.FinishingTouches__questionContainer}>
              <p className={css.FinishingTouches__question}>
                Is there anyone else you'd like to be able to add products to
                your registry?
              </p>
              <p className={css.FinishingTouches__questionSubText}>
                They won't be listed as a co-owner but will have the ability to{' '}
                help you build your registry!
              </p>
            </div>
            <div className={css.FinishingTouches__buttonContainer}>
              <InfoButton
                className={classNames(css.FinishingTouches__button, {
                  [css.FinishingTouches__button__active]: showCollaboratorForm,
                  [css.FinishingTouches__button__inactive]:
                    showCollaboratorForm === false,
                })}
                type="button"
                onClick={() => setShowCollaboratorForm(true)}
              >
                Yes
              </InfoButton>
              <InfoButton
                className={classNames(css.FinishingTouches__button, {
                  [css.FinishingTouches__button__active]:
                    showCollaboratorForm === false,
                  [css.FinishingTouches__button__inactive]:
                    showCollaboratorForm,
                })}
                type="button"
                onClick={() => setShowCollaboratorForm(false)}
              >
                No
              </InfoButton>
            </div>
            <div
              className={classNames(css.FinishingTouches__inputContainer, {
                [css.FinishingTouches__inputContainer__show]:
                  showCollaboratorForm,
              })}
            >
              <Input
                inputRef={register}
                label="Collaborator's first name"
                name="collaboratorFirstName"
                type="text"
              />
              <Input
                inputRef={register}
                label="Collaborator's last name"
                name="collaboratorLastName"
                type="text"
              />
              <Input
                error={collaboratorEmailError}
                help={
                  showCollaboratorFormEmailHelperText
                    ? 'Email address is required for them to add items to your registry.'
                    : ''
                }
                inputRef={register({
                  required: showCollaboratorForm,
                  validate: (email: string) =>
                    validateEmail(email, watch('partnerEmailAddress')),
                })}
                label="Collaborator's email address"
                name="collaboratorEmailAddress"
                type="email"
                onBlur={() => setShowCollaboratorFormEmailHelperText(false)}
                onFocus={() => setShowCollaboratorFormEmailHelperText(true)}
              />
            </div>
          </div>
          <div className={css.FinishingTouches__submitButtonContainer}>
            <InfoButton
              block
              pill
              className={css.FinishingTouches__submitButton}
              disabled={submitting}
              submitting={submitting}
              type="submit"
            >
              All Set!
            </InfoButton>
            {!isInActEx5PartnerExperiment && (
              <LinkButton
                block
                className={css.FinishingTouches__skipButton}
                type="button"
                onClick={onSkip}
              >
                Skip for now
              </LinkButton>
            )}
          </div>
        </Form>
      </div>
    </Slide>
  )
}
